import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import TopBar from "../components/TopBar";
import {
  buildHealthyHabitsDestination,
  createLegacyHandoffAttempt,
  generateLegacyHandoffNonce,
  isValidLegacyV2Bearer,
  LEGACY_WORKOUT_ACCESS_PATH,
  readPendingLegacyBearer,
} from "../features/workoutPlan/workoutPlanLegacyCompatibility";
import "./WorkoutPlan.css";

const defaultReplaceLocation = (destination) => window.location.replace(destination);

function failureMessage(reason) {
  if (reason === "invalid-v2-format") return "This legacy access link is not compatible with current secure access.";
  if (reason === "popup-blocked") return "The secure Healthy Habits window could not open. Allow popups, then try again.";
  if (reason === "child-closed") return "The Healthy Habits window closed before secure access completed. Try again.";
  return "Secure access could not be completed. Try again.";
}

export default function WorkoutPlanLegacyCompatibility({
  windowRef = typeof window === "undefined" ? null : window,
  storageRef = typeof window === "undefined" ? null : window.sessionStorage,
  replaceLocation = defaultReplaceLocation,
  nonceFactory = generateLegacyHandoffNonce,
  createAttempt = createLegacyHandoffAttempt,
}) {
  const location = useLocation();
  const isAccessRoute = location.pathname === LEGACY_WORKOUT_ACCESS_PATH;
  const tokenStillInUrl = new URLSearchParams(location.search).has("token");
  const [hasPendingBearer] = useState(() => !!readPendingLegacyBearer(storageRef));
  const [transferState, setTransferState] = useState({ status: "idle", reason: null });
  const attemptRef = useRef(null);
  const shouldLaunch = isAccessRoute && (hasPendingBearer || tokenStillInUrl);

  useEffect(() => {
    if (!shouldLaunch) replaceLocation(buildHealthyHabitsDestination(location));
  }, [location, replaceLocation, shouldLaunch]);

  useEffect(() => () => { attemptRef.current?.dispose(); }, []);

  function startTransfer() {
    const bearer = readPendingLegacyBearer(storageRef);
    if (!bearer || !windowRef) {
      setTransferState({ status: "failed", reason: "missing-bearer" });
      return;
    }
    if (!isValidLegacyV2Bearer(bearer)) {
      setTransferState({ status: "failed", reason: "invalid-v2-format" });
      return;
    }
    attemptRef.current?.dispose({ closeChild: true });
    let nonce;
    try { nonce = nonceFactory(); }
    catch (_error) { setTransferState({ status: "failed", reason: "secure-random-unavailable" }); return; }

    setTransferState({ status: "opening", reason: null });
    attemptRef.current = createAttempt({
      bearer, nonce, windowRef, storageRef,
      onState(nextState) {
        setTransferState(nextState);
      },
    });
  }

  if (!shouldLaunch) {
    return <main className="workout-compatibility-redirect" aria-live="polite"><p role="status">Opening Workout Plan in Healthy Habits…</p></main>;
  }

  const busy = ["opening", "waiting", "transferring"].includes(transferState.status);
  const failed = transferState.status === "failed";
  return (
    <div className="workout-page">
      <TopBar />
      <main className="workout-shell workout-compatibility-shell">
        <section className="workout-state workout-compatibility-card" aria-live="polite">
          <p className="workout-compatibility-eyebrow">Workout Plan</p>
          <h1>Workout Plan has moved</h1>
          <p>Your private Workout Plan is now available in Healthy Habits.</p>
          {tokenStillInUrl && !hasPendingBearer ? <p role="alert">Secure access could not be prepared in this browser.</p> : transferState.status === "accepted" ? <p role="status">Healthy Habits is ready in the new window.</p> : (
            <>
              {transferState.status === "opening" && <p role="status">Opening the secure Healthy Habits receiver…</p>}
              {transferState.status === "waiting" && <p role="status">Secure receiver opened. Waiting for Healthy Habits…</p>}
              {transferState.status === "transferring" && <p role="status">Secure access delivered. Waiting for receiver confirmation…</p>}
              {failed && <p role="alert">{failureMessage(transferState.reason)}</p>}
              <button type="button" className="workout-compatibility-action" onClick={startTransfer} disabled={busy}>
                {failed ? "Retry secure transfer" : "Continue to Healthy Habits"}
              </button>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
